
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import CalculatorDisplay from './CalculatorDisplay';
import CalculatorKeypad from './CalculatorKeypad';
import { performCalculation } from './utils/calculatorUtils';

const StandardCalculator = () => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [history, setHistory] = useState<string[]>([]);

  const clearAll = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDot = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const result = calculateResult(previousValue, inputValue, operation);
      setPreviousValue(result);
      setDisplay(result.toString());
      
      // Add to history
      const calculation = `${previousValue} ${operation} ${inputValue} = ${result}`;
      setHistory([...history, calculation]);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculateResult = (previousValue: number, inputValue: number, operation: string): number => {
    switch (operation) {
      case '+':
        return previousValue + inputValue;
      case '-':
        return previousValue - inputValue;
      case '*':
        return previousValue * inputValue;
      case '/':
        return previousValue / inputValue;
      default:
        return inputValue;
    }
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const result = calculateResult(previousValue, inputValue, operation);
      
      // Add to history
      const calculation = `${previousValue} ${operation} ${inputValue} = ${result}`;
      setHistory([...history, calculation]);
      
      setDisplay(result.toString());
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden shadow-lg">
        <CardContent className="p-0">
          <CalculatorDisplay value={display} />
          <CalculatorKeypad 
            onDigitClick={inputDigit}
            onOperatorClick={performOperation}
            onDecimalClick={inputDot}
            onClearClick={clearAll}
            onEqualsClick={handleEquals}
          />
        </CardContent>
      </Card>
      
      {history.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2">Calculation History</h3>
            <div className="max-h-60 overflow-y-auto">
              <ul className="space-y-1">
                {history.map((item, index) => (
                  <li key={index} className="text-sm border-b pb-1">{item}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StandardCalculator;
